/**
 * Reloads the page once when a lazily loaded chunk can no longer be fetched.
 *
 * Every deploy renames the hashed chunks and Firebase Hosting drops the old
 * files. A tab opened before the deploy then fails on its next route change
 * ("Failed to fetch dynamically imported module"). Vite reports that as
 * `vite:preloadError`; a reload fetches the current index.html and with it
 * the current chunk names. This is the handling Vite documents for exactly
 * this case: https://vite.dev/guide/build#load-error-handling
 *
 * The guard keeps a genuinely missing chunk from reloading forever: a second
 * failure within the window is left alone and surfaces as an error.
 */

const STORAGE_KEY = 'cockpit:preload-error-reload-at';

/** How long after a reload a further preload error is not answered with another one. */
export const RELOAD_GUARD_MS = 60_000;

export function shouldReloadAfterPreloadError(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || now - lastReloadAt > RELOAD_GUARD_MS;
}

interface PreloadErrorReloadDeps {
  target?: EventTarget;
  storage?: Storage;
  reload?: () => void;
  now?: () => number;
}

function readLastReloadAt(storage: Storage): number | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function rememberReload(storage: Storage, at: number): boolean {
  try {
    storage.setItem(STORAGE_KEY, String(at));
    return true;
  } catch {
    return false;
  }
}

/**
 * Starts listening for `vite:preloadError`. Returns a function that stops it.
 */
export function installPreloadErrorReload({
  target = window,
  storage = window.sessionStorage,
  reload = () => window.location.reload(),
  now = Date.now,
}: PreloadErrorReloadDeps = {}): () => void {
  const onPreloadError = (event: Event) => {
    const at = now();
    if (!shouldReloadAfterPreloadError(readLastReloadAt(storage), at)) return;
    // Without a remembered timestamp there is no guard against a reload loop.
    if (!rememberReload(storage, at)) return;
    // Keeps Vite from rethrowing while the page is already on its way out.
    event.preventDefault();
    reload();
  };

  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
